import { Button, keyframes, Stack, Typography, useTheme } from "@mui/material";
import { useMemo } from "react";
import type { CurrencyEnum } from "web_myplann/Infrastructure/Api/Api";
import { PortfolioReportingOverviewTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { PropertyWidgetTooltip } from "web_myplann/Shared/Components/PropertyWidgetTooltip";
import { moneyFormatter } from "web_myplann/Shared/Utils/formatters";
import { DashboardCardValue } from "./DashboardCardValue";
import type { DashboardType } from "./DashboardTitleCard";
import { EvaluationChip } from "./EvaluationChip";

const slideOutLeft = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-100%); }
`;

const slideInFromRight = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
`;

const slideOutRight = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(100%); }
`;

const slideInFromLeft = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(0); }
`;

type AnimationDirection = "left" | "right" | null;

type DashboardAnimatedContentProps = {
	dashboardData: DashboardType;
	isAnimating: boolean;
	isPrevious: boolean;
	animationDirection: AnimationDirection;
	isDashboardDetail?: boolean;
	navigateToDashboardDetail?: () => void;
	onTooltipToggle?: (open: boolean) => void;
};

export const DashboardAnimatedContent = ({
	dashboardData,
	isAnimating,
	isPrevious,
	animationDirection,
	isDashboardDetail,
	navigateToDashboardDetail,
	onTooltipToggle,
}: DashboardAnimatedContentProps) => {
	const { t } = useResource();
	const { palette } = useTheme();

	const isMissingValueWithItems =
		dashboardData.value === null && dashboardData.itemCountWithoutValue > 0;

	const { evaluation } = dashboardData;
	const showEvaluationChip =
		!isMissingValueWithItems &&
		evaluation != null &&
		dashboardData.overviewType !== PortfolioReportingOverviewTypeEnum.Total;

	const currencySign = useMemo(
		() =>
			dashboardData.currency
				? t(
						Resources.PortfolioDetail.SharedLabels.Currency[
							dashboardData.currency as CurrencyEnum
						],
					)
				: "",
		[dashboardData.currency, t],
	);

	const getAnimation = () => {
		if (!isAnimating) return "none";

		if (isPrevious) {
			return animationDirection === "right"
				? `${slideOutLeft} 0.3s ease-in-out`
				: `${slideOutRight} 0.3s ease-in-out`;
		}
		return animationDirection === "right"
			? `${slideInFromRight} 0.3s ease-in-out`
			: `${slideInFromLeft} 0.3s ease-in-out`;
	};

	return (
		<Stack
			key={dashboardData.id}
			sx={{
				position: "absolute",
				width: "100%",
				top: 0,
				left: 0,
				bottom: 0,
				alignItems: "center",
				justifyContent: isDashboardDetail ? "center" : "flex-start",
				pt: isDashboardDetail ? 0 : "26px",
				animation: getAnimation(),
			}}
		>
			<Stack alignItems="center" justifyContent="flex-start" gap={0.8}>
				<Stack alignItems="center">
					{!isDashboardDetail && (
						<Stack direction="row" alignItems="center" gap={0.4}>
							<Typography
								variant="deprecatedBodyStrong"
								color={palette.darkmodeText.white}
								lineHeight={"20px"}
								zIndex={1}
							>
								{dashboardData.type}
							</Typography>
							<PropertyWidgetTooltip
								title={t(Resources.Dashboard.TitleCard.tooltip)}
								onToggle={onTooltipToggle}
								slotProps={{
									popper: {
										modifiers: [
											{ name: "offset", options: { offset: [-50, -20] } },
										],
									},
								}}
							/>
						</Stack>
					)}
					<DashboardCardValue
						dashboardData={dashboardData}
						isMissingValueWithItems={isMissingValueWithItems}
					/>
				</Stack>
				{showEvaluationChip && <EvaluationChip evaluation={evaluation} />}

				{dashboardData.netValue != null && (
					<Stack direction="row" alignItems="center" zIndex={1}>
						<Typography
							fontSize={14}
							fontWeight={400}
							color={palette.darkmodeText.white}
							sx={{ fontVariantNumeric: "tabular-nums" }}
						>
							{t(Resources.Dashboard.TitleCard.netValue, {
								value: moneyFormatter(
									Math.round(dashboardData.netValue),
									currencySign,
									`0 ${currencySign}`,
								),
							})}
						</Typography>
						<PropertyWidgetTooltip
							title={t(Resources.Dashboard.TitleCard.netValueTooltip)}
							onToggle={onTooltipToggle}
							size={14}
							slotProps={{
								popper: {
									modifiers: [
										{ name: "offset", options: { offset: [-50, -20] } },
									],
								},
							}}
						/>
					</Stack>
				)}
			</Stack>

			{navigateToDashboardDetail && (
				<Button
					type="button"
					sx={{
						padding: "4px 16px",
						position: "absolute",
						bottom: "40px",
						height: "32px",
						fontSize: "14px",
						fontWeight: 600,
						lineHeight: "20px",
						backgroundColor: palette.darkmodeText.white,
						color: dashboardData.gradientFrom,
						zIndex: 1,
					}}
					onClick={navigateToDashboardDetail}
				>
					{t(Resources.Dashboard.TitleCard.title)}
				</Button>
			)}
		</Stack>
	);
};
